import type { DsdEditorValueMatch } from "@/apis/dsd-editor/route";
import type { PlainDsdNoteReferenceStatus } from "@/types/plain-dsd-table";
import queries from "@/queries";
import {
  buildCompanyNameCellSegments,
  buildCompanyNameOccurrenceLocations,
  buildCompanyNameParagraphSegments,
  getCompanyNameCautionChunkIds,
  getCompanyNameCautionOccurrenceIds,
} from "@/utils/dsd-editor/company-names";
import {
  type DsdEditorParsedItem,
  getRenderedCellIds,
  getReportParsedItems,
  getSelectedCellSummaryById,
} from "@/utils/dsd-editor/document-items";
import {
  type DsdNoteReferenceCandidateTone,
  getNoteReferenceDisplayStatus,
  NOTE_REFERENCE_CANDIDATE_TONE,
  strongerCandidateTone,
  strongerNoteReferenceDisplayStatus,
} from "@/utils/dsd-editor/note-reference";
import {
  getHardRuleCountByCellId,
  getInheritedReviewCellIds,
  getOccurrenceIds,
} from "@/utils/dsd-editor/note-reference-review";
import {
  buildMatchedParagraphSegments,
  getMatchedParagraphValueIds,
} from "@/utils/dsd-editor/matched-paragraphs";
import {
  countCorrectionCategories,
  type DsdEditorCorrectionItem,
  getCorrectionItems,
  getSpellcheckBlockByCellId as getCorrectionBlockByCellId,
  getSpellcheckBlockByItemId as getCorrectionBlockByItemId,
} from "@/utils/dsd-editor/spellcheck";
import * as Sentry from "@sentry/react";
import {
  assertPlainDsdTableLabelPaths,
  findPlainDsdHeaderRowViolation,
} from "@/utils/dsd-editor/cell-label-paths";
import { useBetaPreference } from "@/hooks/use-beta-preference";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

const UNIT_DIFFERENCE_V2_KEY = "dsd-editor:unit-difference-v2";
const HARD_RULE_HIGHLIGHT_KEY = "dsd-editor:hard-rule-highlight";

// 전역 기본이 staleTime 0이라 창 포커스/visibility 복귀 때마다 무거운 문서 전체를
// 다시 받는다. 리포트를 바꾸는 건 검토 판단 저장뿐이고, 그건 캐시를 직접 고치고
// 충돌(409) 때만 다시 받으므로(use-mapping-review) 캐시를 유지해도 안전.
const DSD_EDITOR_QUERY_CACHE_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
} as const;

/**
 * 백엔드 계약 검사. 위반(throw)이면 Sentry 에 보내고 null — 호출측은 리뷰 로드 실패로 올린다.
 * 반쪽짜리 데이터로 화면이 조용히 성공하거나, 렌더 중 throw 로 앱 전체가 루트 에러 화면으로 가는 것을 막는다.
 */
const checkContract = <T>(check: () => T): T | null => {
  try {
    return check();
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

/** 교정 분류 계약. 통과하면 맞춤법·띄어쓰기 수 — 하위 패널의 getCorrectionCategory 호출은 안전하다 */
export const checkCorrectionContract = (
  items: readonly DsdEditorCorrectionItem[],
) => checkContract(() => countCorrectionCategories(items));

/** 셀 행·열 표기 경로 계약. 통과하면 화면의 getDsdCellAxisLabel 은 throw 하지 않는다 */
export const checkCellLabelPathContract = (
  items: readonly DsdEditorParsedItem[],
) =>
  checkContract(() => {
    for (const item of items)
      if (item.kind === "table") assertPlainDsdTableLabelPaths(item.table);
    return true;
  }) !== null;

/** 열 머리글 표시 위반은 문서를 막지 않는다 — 그 표만 고정을 끄고(PlainDsdTableView) 표마다 Sentry 에 보고한다 */
export const reportHeaderRowViolations = (
  items: readonly DsdEditorParsedItem[],
) => {
  for (const item of items) {
    if (item.kind !== "table") continue;
    const violation = findPlainDsdHeaderRowViolation(item.table);
    if (violation) Sentry.captureException(new Error(violation));
  }
};

export const getDsdEditorReviewErrorMessage = () =>
  "DSD 리뷰에 실패했습니다. 반복하여 실패할 경우 문의해주세요.";

/** 주석 후보 셀 색 = 매칭된 소스(메인) 셀 상태를 그대로 물려받는다. 여러 소스에 걸리면 센 색이 남는다 */
export const getNoteReferenceCandidateStatusByCellId = (
  valueMatches: DsdEditorValueMatch[],
  statusByCellId: ReadonlyMap<string, PlainDsdNoteReferenceStatus>,
  renderedCellIds: ReadonlySet<string>,
) => {
  const map = new Map<string, DsdNoteReferenceCandidateTone>();

  for (const match of valueMatches) {
    const sourceStatus = getOccurrenceIds(match.source)
      .map((id) => statusByCellId.get(id))
      .find((status): status is PlainDsdNoteReferenceStatus => !!status);
    const tone = sourceStatus
      ? NOTE_REFERENCE_CANDIDATE_TONE[sourceStatus]
      : "positive";

    for (const candidate of match.candidates) {
      for (const cellId of getOccurrenceIds(candidate.occurrence)) {
        if (!renderedCellIds.has(cellId)) continue;
        const current = map.get(cellId);
        map.set(cellId, current ? strongerCandidateTone(current, tone) : tone);
      }
    }
  }

  return map;
};

export const useDsdEditorReviewDocumentData = (documentId: string) => {
  // 베타: 단수차이 V2 — 켜면 report 조회에 unit_difference_v2=true (토글 시 refetch)
  const { enabled: unitDifferenceV2, setEnabled: setUnitDifferenceV2 } =
    useBetaPreference(UNIT_DIFFERENCE_V2_KEY);
  // 베타: 하드룰 연결 셀 빗금 — 켠 동안만 표·패널에 표시 (조회에는 영향 없음)
  const { enabled: hardRuleHighlight, setEnabled: setHardRuleHighlight } =
    useBetaPreference(HARD_RULE_HIGHLIGHT_KEY);
  const reportQuery = useQuery({
    ...queries.dsdEditor(documentId)._ctx.report(unitDifferenceV2),
    ...DSD_EDITOR_QUERY_CACHE_OPTIONS,
  });
  const correctionQuery = useQuery({
    ...queries.dsdEditor(documentId)._ctx.spellcheck(),
    ...DSD_EDITOR_QUERY_CACHE_OPTIONS,
    enabled: reportQuery.isSuccess,
  });

  const report = reportQuery.data;
  const displayItems = useMemo(
    () => getReportParsedItems(report?.chunks ?? [], report?.sections ?? []),
    [report?.chunks, report?.sections],
  );
  const sections = useMemo(() => {
    if (report?.sections?.length) return report.sections;

    return [...new Set(displayItems.map((item) => item.sectionName))];
  }, [report?.sections, displayItems]);
  const valueMatches = useMemo(
    () => report?.valueMatches ?? [],
    [report?.valueMatches],
  );
  const noteValueMatches = useMemo(
    () => report?.noteValueMatches ?? [],
    [report?.noteValueMatches],
  );
  const noteReferenceChecks = useMemo(
    () => report?.noteReferenceChecks ?? [],
    [report?.noteReferenceChecks],
  );
  const matchedParagraphs = useMemo(
    () => report?.matchedParagraphs ?? [],
    [report?.matchedParagraphs],
  );
  const matchedParagraphSegments = useMemo(
    () => buildMatchedParagraphSegments(matchedParagraphs),
    [matchedParagraphs],
  );
  // 문단 value occurrence를 셀처럼 상태/선택에 편입 (renderedCellIds에 포함)
  const renderedCellIds = useMemo(() => {
    const ids = new Set(getRenderedCellIds(displayItems));
    for (const id of getMatchedParagraphValueIds(matchedParagraphs)) {
      ids.add(id);
    }
    return ids;
  }, [displayItems, matchedParagraphs]);
  const selectedCellSummaryById = useMemo(
    () => getSelectedCellSummaryById(displayItems),
    [displayItems],
  );
  const correctionBlocks = useMemo(
    () => correctionQuery.data?.blocks ?? [],
    [correctionQuery.data?.blocks],
  );
  const correctionBlockByItemId = useMemo(
    () => getCorrectionBlockByItemId(displayItems, correctionBlocks),
    [displayItems, correctionBlocks],
  );
  const correctionBlockByCellId = useMemo(
    () => getCorrectionBlockByCellId(correctionBlocks, renderedCellIds),
    [renderedCellIds, correctionBlocks],
  );
  const correctionItems = useMemo(
    () =>
      getCorrectionItems(
        displayItems,
        correctionBlockByItemId,
        correctionBlockByCellId,
      ),
    [displayItems, correctionBlockByCellId, correctionBlockByItemId],
  );
  // 리뷰 화면이 계약 검사 결과를 isError 하나로 본다
  const correctionCategoryCounts = useMemo(
    () => checkCorrectionContract(correctionItems),
    [correctionItems],
  );
  const cellLabelPathsValid = useMemo(
    () => checkCellLabelPathContract(displayItems),
    [displayItems],
  );
  useEffect(() => reportHeaderRowViolations(displayItems), [displayItems]);
  // 기업명 분석 — 응답에 없으면 빈 배열
  const companyNameReferences = useMemo(
    () => report?.companyNameReferences ?? [],
    [report?.companyNameReferences],
  );
  const companyNameBlocks = useMemo(
    () => report?.companyNameMatchedBlocks ?? [],
    [report?.companyNameMatchedBlocks],
  );
  const companyNameParagraphSegments = useMemo(
    () => buildCompanyNameParagraphSegments(companyNameBlocks),
    [companyNameBlocks],
  );
  const companyNameCellSegments = useMemo(
    () => buildCompanyNameCellSegments(companyNameBlocks),
    [companyNameBlocks],
  );
  const companyNameCautionOccurrenceIds = useMemo(
    () => getCompanyNameCautionOccurrenceIds(companyNameReferences),
    [companyNameReferences],
  );
  const companyNameCautionChunkIds = useMemo(
    () =>
      getCompanyNameCautionChunkIds(
        companyNameBlocks,
        companyNameCautionOccurrenceIds,
      ),
    [companyNameBlocks, companyNameCautionOccurrenceIds],
  );
  const companyNameLocationByOccurrenceId = useMemo(
    () =>
      buildCompanyNameOccurrenceLocations(
        companyNameBlocks,
        new Map((report?.chunks ?? []).map((chunk) => [chunk.id, chunk.title])),
      ),
    [companyNameBlocks, report?.chunks],
  );
  const noteReferenceStatusByCellId = useMemo(() => {
    const map = new Map<string, PlainDsdNoteReferenceStatus>();
    for (const check of noteReferenceChecks) {
      const status = getNoteReferenceDisplayStatus(check);

      for (const cellId of getOccurrenceIds(check.source)) {
        if (!renderedCellIds.has(cellId)) continue;

        const current = map.get(cellId);
        map.set(
          cellId,
          current
            ? strongerNoteReferenceDisplayStatus(current, status)
            : status,
        );
      }
    }

    return map;
  }, [noteReferenceChecks, renderedCellIds]);
  const hardRuleCountByCellId = useMemo(
    () => getHardRuleCountByCellId(noteReferenceChecks),
    [noteReferenceChecks],
  );

  const inheritedReviewCellIds = useMemo(
    () => getInheritedReviewCellIds(noteReferenceChecks),
    [noteReferenceChecks],
  );

  return {
    hardRuleCountByCellId,
    inheritedReviewCellIds,
    report,
    displayItems,
    sections,
    valueMatches,
    noteValueMatches,
    renderedCellIds,
    selectedCellSummaryById,
    correctionBlockByItemId,
    correctionBlockByCellId,
    correctionItems,
    correctionCount:
      correctionBlockByItemId.size + correctionBlockByCellId.size,
    correctionReady: correctionQuery.isSuccess,
    correctionPending:
      reportQuery.isSuccess &&
      (correctionQuery.isPending || correctionQuery.isFetching),
    noteReferenceChecks,
    noteReferenceStatusByCellId,
    matchedParagraphSegments,
    companyNameReferences,
    companyNameParagraphSegments,
    companyNameCellSegments,
    companyNameCautionOccurrenceIds,
    companyNameCautionChunkIds,
    companyNameLocationByOccurrenceId,
    isLoading: reportQuery.isLoading,
    // 백엔드 계약 위반(교정 분류·셀 표기 경로)도 로드 실패와 같은 UX(실패 모달 + 탭 닫힘)
    isError:
      reportQuery.isError ||
      correctionCategoryCounts === null ||
      !cellLabelPathsValid,
    isEmpty: displayItems.length === 0,
    errorMessage: getDsdEditorReviewErrorMessage(),
    retry: reportQuery.refetch,
    unitDifferenceV2,
    setUnitDifferenceV2,
    hardRuleHighlight,
    setHardRuleHighlight,
  };
};
